import React from 'react';
import BrandingFooter from '../common/BrandingFooter';
import TeamBadge, { pickTeamLogo } from '../common/TeamBadge';
import MatchWidgetShell from '../common/MatchWidgetShell';
import MatchScoreHero from '../common/MatchScoreHero';
import { coachInitials, normalizeCoach } from '../common/coachUtils';
import { useWidgetLabels } from '../common/WidgetLabelsContext';

function DetailChip({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="sw-coach-chip">
      <span className="sw-coach-chip__icon" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="sw-coach-chip__label">{label}</p>
        <p className="sw-coach-chip__value">{value}</p>
      </div>
    </div>
  );
}

function CoachProfileCard({
  side,
  teamName,
  opponent,
  logoUrl,
  coach,
  formation,
  venue,
  t,
}) {
  const c = normalizeCoach(coach);
  if (!c?.name) return null;

  const isHome = side === 'home';
  const initials = coachInitials(c.name);

  return (
    <article className={`sw-coach-profile ${isHome ? 'sw-coach-profile--home' : 'sw-coach-profile--away'}`}>
      <div className="sw-coach-profile__banner">
        <span className="sw-coach-profile__side">{isHome ? t('home') : t('away')}</span>
        <TeamBadge name={teamName} logoUrl={logoUrl} size={36} />
      </div>

      <div className="sw-coach-profile__body">
        <div
          className="sw-coach-avatar"
          style={{
            borderColor: isHome ? 'var(--sw-accent)' : '#3b82f6',
            background: isHome
              ? 'color-mix(in srgb, var(--sw-accent) 18%, var(--sw-card))'
              : 'color-mix(in srgb, #3b82f6 18%, var(--sw-card))',
            color: isHome ? 'var(--sw-accent)' : '#60a5fa',
          }}
        >
          {initials}
        </div>

        <h3 className="sw-coach-profile__name">{c.name}</h3>
        <p className="sw-coach-profile__role">{c.role || t('headCoach')}</p>

        <div className="sw-coach-profile__chips">
          {c.nationality && (
            <DetailChip icon="🌍" label={t('nationality')} value={c.nationality} />
          )}
          {c.age && <DetailChip icon="📅" label={t('age')} value={c.age} />}
          {c.since && <DetailChip icon="📋" label={t('appointed')} value={c.since} />}
          {formation && (
            <DetailChip icon="⚽" label={t('formation')} value={formation} />
          )}
          {opponent && (
            <DetailChip icon="🆚" label={t('opponent')} value={opponent} />
          )}
        </div>

        {venue && isHome && (
          <p className="sw-coach-profile__venue">
            <span aria-hidden>📍</span> {t('homeVenue')}: {venue}
          </p>
        )}
      </div>
    </article>
  );
}

export default function CoachCard({
  showBranding = false,
  competitionLabel = '',
  featuredMatch = null,
}) {
  const t = useWidgetLabels();
  const m = featuredMatch || {};
  const coaches = m.coaches;

  const venue = m.venue
    ? `${m.venue}${m.venueCity ? `, ${m.venueCity}` : ''}`
    : null;

  const homeFormation = m.lineups?.home?.formation;
  const awayFormation = m.lineups?.away?.formation;

  const hasCoach = coaches?.home || coaches?.away;

  return (
    <div>
      <MatchWidgetShell competitionLabel={competitionLabel} subtitle={t('matchCoaches')}>
        <MatchScoreHero match={m} compact />

        {hasCoach ? (
          <>
            <div className="px-4 pt-2 pb-1 flex items-center justify-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--sw-muted)' }}>
                {t('touchline')}
              </span>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CoachProfileCard
                side="home"
                teamName={m.home}
                opponent={m.away}
                logoUrl={pickTeamLogo(m, 'home')}
                coach={coaches.home}
                formation={homeFormation}
                venue={venue}
                t={t}
              />
              <CoachProfileCard
                side="away"
                teamName={m.away}
                opponent={m.home}
                logoUrl={pickTeamLogo(m, 'away')}
                coach={coaches.away}
                formation={awayFormation}
                venue={null}
                t={t}
              />
            </div>
            {(venue || m.referee) && (
              <div className="px-4 pb-4">
                <div className="sw-coach-match-footer">
                  {venue && (
                    <p>
                      <span className="font-bold uppercase text-[9px] tracking-wider opacity-70">
                        {t('venue')}
                      </span>
                      <br />
                      {venue}
                    </p>
                  )}
                  {m.referee && (
                    <p>
                      <span className="font-bold uppercase text-[9px] tracking-wider opacity-70">
                        {t('referee')}
                      </span>
                      <br />
                      {m.referee}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-xs py-10" style={{ color: 'var(--sw-muted)' }}>
            {t('noData')}
          </p>
        )}
      </MatchWidgetShell>
      {showBranding && <BrandingFooter />}
    </div>
  );
}
